import React from 'react';
import {CrudConfig} from '@src/model/table';
import {useStores} from '@src/components/store';
import {DeviceRow, InviteRequest} from '@src/dtos/device.dto';
import {isDuplicate} from '@src/utils/validationUtils';
import {MAX_REGISTERED_DEVICES} from '@src/utils/constants';
import {formatPhone} from '@src/utils/phone-util';
import {EventType} from '@src/dtos/analytics.dto';
import {DevicesRowActions} from './devices-row-actions';

const devicesDataChangesHandler = {
    set: (obj: any, prop: string, value: any) => {
        obj[prop] = prop === 'phoneNumber' ? String(value ?? '').replace(/\D/g, '').slice(0, 10) : value;
        return true;
    }
};

const toRequest = (data: DeviceRow): InviteRequest => ({
    phoneNumber: data.phoneNumber,
    userEmail: data.userEmail,
    userName: data.userName?.trim() || undefined
});

export const useDevicesCrudConfig = (): CrudConfig<DeviceRow> => {
    const {devicesStore, userStore, uiStore} = useStores();

    return {
        createNewText: 'Add device',
        addModalTitle: 'Add iOS device',
        buttonClass: 'btn-add-device',
        entityName: 'device',
        actionTooltips: {
            edit: 'Edit employee name',
            remove: 'Delete device',
            className: 'cru-device-menu-tooltip',
            placement: 'bottom',
        },
        rowLimit: MAX_REGISTERED_DEVICES,
        disableEditReason: userStore.isExternalUser ? 'This action is allowed for AT&T users only' : undefined,
        displayInlineActions: !userStore.isExternalUser,
        newRecord: {
            phoneNumber: '',
            userEmail: '',
            userName: ''
        },
        isDuplicated: (record: DeviceRow, originalRecord: DeviceRow | undefined, keys: string[]): boolean =>
            isDuplicate(record, originalRecord, devicesStore.devices, keys),
        saveCb: async (data: DeviceRow) => {
            await devicesStore.create(toRequest(data));
            uiStore.sendGeneralEvent(EventType.LinkClicked, { button: 'addDevice', location: 'addDeviceModal' });
        },
        updateCb: async (updatedRecord: DeviceRow) => {
            await devicesStore.edit(updatedRecord.inviteId, toRequest(updatedRecord));
            uiStore.sendGeneralEvent(EventType.LinkClicked, { button: 'saveDevice', location: 'editDeviceModal' });
        },
        removeCb: async (data: DeviceRow) => {
            await devicesStore.revoke(data.inviteId);
            uiStore.sendGeneralEvent(EventType.LinkClicked, { button: 'deleteDevice', location: 'deleteDeviceModal' });
        },
        removeModal: {
            title: 'Delete device',
            body: (data: DeviceRow) => (
                <div>
                    <span>The following device will be removed from service:</span>
                    <span>
                        <br/>{formatPhone(data.phoneNumber)}
                        <br/>{data.userEmail}
                        {data.userName && (
                            <>
                                <br/>{data.userName}
                            </>
                        )}
                    </span>
                </div>
            ),
            successMsg: (data: DeviceRow) => `${formatPhone(data.phoneNumber)} was deleted`
        },
        customActions: (rowData: DeviceRow) => <DevicesRowActions row={rowData}/>,

        dataChangesHandler: devicesDataChangesHandler
    };
};
